import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { STALE_TIME } from '~/domains/contest/constants/query';

import type { PropsFiltersType, PublicGame } from '../../services/interfaces/propsService/api';
import { getLeagueFormat } from '../../services/interfaces/propsService/api/leagues';
import { getFilters } from '../../services/props.service.api';
import QuickPickQueryKeys from '../constants/query';

import { useQuickPicksSettings } from './useQuickPicksSettings';

export type FlattenedType = PropsFiltersType & {
  leagueId: string;
};

export type LeagueGameFormat = 'matchup' | 'tournament';

export function isTeamMatchupGame(game: PublicGame): boolean {
  return getLeagueFormat(game.league) === 'team';
}

export function getGameDisplayLabel(game: {
  name: string;
  home?: { alias: string };
  away?: { alias: string };
}): string {
  if (game.home && game.away) {
    return `${game.away.alias} @ ${game.home.alias}`;
  }
  return game.name;
}

export const useQuickPicksFilters = () => {
  const settingsQuery = useQuickPicksSettings();

  const { data, isLoading, error } = useQuery({
    queryKey: QuickPickQueryKeys.QUICK_PICKS_FILTERS(),
    queryFn: () => getFilters(settingsQuery.data?.state_config.disallowed ?? []),
    staleTime: STALE_TIME,
    enabled: !!settingsQuery.data,
  });

  const flattenedTypes: FlattenedType[] = useMemo(
    () =>
      data?.filters.leagues.flatMap((league) =>
        league.types.map((type) => ({
          ...type,
          leagueId: league.id,
        }))
      ) || [],
    [data]
  );

  const { gamesByLeague, leagueGameFormat } = useMemo(() => {
    const byLeague: Record<string, PublicGame[]> = {};
    const formatByLeague: Record<string, LeagueGameFormat> = {};
    data?.filters.leagues.forEach((league) => {
      const games = league.games ?? [];
      byLeague[league.id] = games;
      const firstGame = games[0];
      formatByLeague[league.id] =
        !firstGame || isTeamMatchupGame(firstGame) ? 'matchup' : 'tournament';
    });
    return { gamesByLeague: byLeague, leagueGameFormat: formatByLeague };
  }, [data]);

  return { data, isLoading, error, flattenedTypes, gamesByLeague, leagueGameFormat };
};
